<template>
  <div class="wheel-overlay font-cutedino" @click="$emit('click')">
    <div class="instructions-layer" ref="instructionsLayer">
      <div class="instructionsText top-instruction" id="topInstruction">
        {{ isTouchScreen ? t('spinningwheel.Tap to spin') : t('spinningwheel.Click to spin') }}
      </div>
      <div v-if="!isTouchScreen && !isSafari" class="instructionsText bottom-instruction" id="bottomInstruction">
        {{ t('spinningwheel.or press ctrl+enter') }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import * as Util from '@utils/Util.js';
import CircleType from 'circletype';

const { t, locale } = useI18n();

const instructionsLayer = ref(null);
const isTouchScreen = ref(false);

let topCircleType = null;
let bottomCircleType = null;
let resizeObserver = null;

const hasWindow = typeof window !== 'undefined';
const isSafari = computed(() => (hasWindow && !!window.safari));
const currentLocale = computed(() => locale.value);

function destroyCircleTypeInstances() {
  if (topCircleType) {
    topCircleType.destroy();
    topCircleType = null;
  }
  if (bottomCircleType) {
    bottomCircleType.destroy();
    bottomCircleType = null;
  }
  // Clean up any CircleType instances attached directly on elements
  const topInstruction = document.getElementById('topInstruction');
  if (topInstruction && topInstruction._circleType) topInstruction._circleType.destroy();
  const bottomInstruction = document.getElementById('bottomInstruction');
  if (bottomInstruction && bottomInstruction._circleType) bottomInstruction._circleType.destroy();
}

function cantBeDisplayedInCircleType(loc) {
  // Languages/scripts that don't render well with CircleType
  return ['ar', 'bn', 'fa', 'gu', 'he', 'hi', 'ta'].includes(loc);
}

function refreshOverlay() {
  const canvas = document.getElementById('wheelCanvas');
  if (!canvas) return; // quietly ignore if wheel not mounted yet

  const side = canvas.offsetWidth || canvas.clientWidth || 0;
  if (!side) return;

  const layer = instructionsLayer.value;
  if (!layer) return;

  // Scale font size based on wheel diameter
  layer.style.fontSize = `${Math.round(side / 20)}px`;

  // Skip CircleType for certain locales/scripts
  if (cantBeDisplayedInCircleType(locale.value)) {
    destroyCircleTypeInstances();
    return;
  }

  const radius = side / 3;

  const topInstruction = document.getElementById('topInstruction');
  if (topInstruction) {
    const topText = (topInstruction.textContent || '').trim();
    if (topText.length > 0) {
      if (!topCircleType) {
        try {
          topCircleType = new CircleType(topInstruction).radius(radius);
        } catch (e) {
          // Fail quietly to avoid noisy logs
        }
      } else {
        try {
          topCircleType.radius(radius);
        } catch (e) {
          // If update fails, try recreating once
          try {
            topCircleType.destroy();
          } catch (_) { }
          topCircleType = null;
          try {
            topCircleType = new CircleType(topInstruction).radius(radius);
          } catch (_) { }
        }
      }
    } else {
      // Text not ready; ensure instance is destroyed
      if (topCircleType) {
        try { topCircleType.destroy(); } catch (_) { }
        topCircleType = null;
      }
    }
  } else {
    // Element missing; ensure instance is destroyed
    if (topCircleType) {
      try { topCircleType.destroy(); } catch (_) { }
      topCircleType = null;
    }
  }

  const bottomInstruction = document.getElementById('bottomInstruction');
  if (bottomInstruction) {
    const bottomText = (bottomInstruction.textContent || '').trim();
    if (bottomText.length > 0) {
      if (!bottomCircleType) {
        try {
          bottomCircleType = new CircleType(bottomInstruction).radius(radius).dir(-1);
        } catch (e) {
          // Fail quietly to avoid noisy logs
        }
      } else {
        try {
          bottomCircleType.radius(radius).dir(-1);
        } catch (e) {
          // If update fails, try recreating once
          try {
            bottomCircleType.destroy();
          } catch (_) { }
          bottomCircleType = null;
          try {
            bottomCircleType = new CircleType(bottomInstruction).radius(radius).dir(-1);
          } catch (_) { }
        }
      }
    } else {
      // Text not ready; ensure instance is destroyed
      if (bottomCircleType) {
        try { bottomCircleType.destroy(); } catch (_) { }
        bottomCircleType = null;
      }
    }
  } else {
    // Element missing (e.g., touch or Safari); ensure instance is destroyed
    if (bottomCircleType) {
      try { bottomCircleType.destroy(); } catch (_) { }
      bottomCircleType = null;
    }
  }
}

onMounted(() => {
  isTouchScreen.value = Util.isTouchScreen();

  // Observe wheel size changes for responsive radius updates
  const canvas = document.getElementById('wheelCanvas');
  if (canvas && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      refreshOverlay();
    });
    resizeObserver.observe(canvas);
  } else if (hasWindow) {
    // Fallback: adjust on window resize
    window.addEventListener('resize', refreshOverlay);
  }

  // Initial layout once mounted
  nextTick(() => {
    refreshOverlay();
  });
});

onBeforeUnmount(() => {
  destroyCircleTypeInstances();
  if (resizeObserver) {
    try {
      resizeObserver.disconnect();
    } catch (_) { }
    resizeObserver = null;
  } else if (hasWindow) {
    window.removeEventListener('resize', refreshOverlay);
  }
});

// Re-apply layout on locale changes (destroy → recreate to handle text changes)
watch(currentLocale, () => {
  destroyCircleTypeInstances();
  nextTick(() => refreshOverlay());
});

// Re-apply layout if touch/desktop branch changes
watch(isTouchScreen, () => {
  destroyCircleTypeInstances();
  nextTick(() => refreshOverlay());
});

// Still expose manual refresh for parent control if needed
defineExpose({ refreshOverlay });
</script>

<style scoped>
.wheel-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.wheel-overlay * {
  pointer-events: auto;
}

.instructions-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.instructionsText {
  width: 100%;
  color: #FFF;
  text-shadow: 0px 0px 10px #000000;
  text-align: center;
  font-weight: 800;
}

.top-instruction {
  position: absolute;
  top: 20%;
  transform: translateY(-50%);
}

.bottom-instruction {
  position: absolute;
  bottom: 20%;
  transform: translateY(50%);
}
</style>
